import React, { useEffect, useRef, useMemo } from "react";
import { Search } from "lucide-react";
import type { LinkedInProspectResponse, Prospect } from "../types/prospects";

type Props = {
  open: boolean;
  loading: boolean;
  error: string | null;
  data: LinkedInProspectResponse | null;
  onClose: () => void;
};

// 🔍 Google Search Fallback Button
function GoogleSearchButton({ brand }: { brand: string }) {
  const query = `site:linkedin.com/in ("SEO" OR "content marketing" OR "link building" OR "outreach") ("works at ${brand}" OR "at ${brand}")`;
  const encodedQuery = encodeURIComponent(query);
  const googleUrl = `https://www.google.com/search?q=${encodedQuery}`;

  return (
    <a
      href={googleUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition"
    >
      <Search className="h-4 w-4" />
      Search on Google directly.
    </a>
  );
}

export default function ProspectsDrawer({ open, loading, error, data, onClose }: Props) {
  const closeBtnRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLElement | null>(null);

  // Build a deduped list (keep original order from API)
  const list: Prospect[] = useMemo(() => {
    const raw = (data?.prospects ?? []) as Prospect[];
    const seen = new Set<string>();
    const unique: Prospect[] = [];
    for (const p of raw) {
      const key = (p.linkedin_url || p.name || "").toLowerCase();
      if (!key || seen.has(key)) continue;
      seen.add(key);
      unique.push(p);
    }
    return unique;
  }, [data]);

  useEffect(() => {
    if (!open) return;

    const onEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };

    const onTab = (e: KeyboardEvent) => {
      if (e.key !== "Tab" || !panelRef.current) return;

      const focusables = panelRef.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
      );
      if (focusables.length === 0) return;

      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement as HTMLElement | null;

      if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      } else if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      }
    };

    document.addEventListener("keydown", onEsc);
    document.addEventListener("keydown", onTab);
    closeBtnRef.current?.focus();

    return () => {
      document.removeEventListener("keydown", onEsc);
      document.removeEventListener("keydown", onTab);
    };
  }, [open, onClose]);

  if (!open) return null;

  const domain = data?.domain ?? "";
  const brand = data?.brand ?? "";
  const count = list.length;

  return (
    <div className="fixed inset-0 z-50">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/30"
        aria-hidden="true"
        onClick={onClose}
      />
      {/* Panel */}
      <aside
        ref={panelRef}
        className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl p-6 overflow-y-auto"
        role="dialog"
        aria-modal="true"
        aria-labelledby="prospects-title"
      >
        <div className="sticky top-0 bg-white pb-3 mb-3 z-10">
          <div className="flex items-start justify-between">
            <div>
              <h3 id="prospects-title" className="text-xl font-semibold text-gray-800">
                LinkedIn Prospects{!loading && !error ? ` (${count})` : ""}
              </h3>
<p className="text-sm text-gray-500 mt-1">
  {domain ? <span className="text-gray-600">{domain}</span> : null}
</p>

            </div>
            <button
              ref={closeBtnRef}
              onClick={onClose}
              className="ml-3 inline-flex items-center justify-center rounded-md p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100"
              aria-label="Close"
              title="Close"
            >
              ✕
            </button>
          </div>
        </div>

        {loading && (
          <div
            className="py-10 text-center text-gray-600"
            role="status"
            aria-live="polite"
          >
            Fetching prospects…
          </div>
        )}

        {error && !loading && (
          <div
            className="p-3 mb-4 rounded-md bg-red-50 text-red-700 text-sm"
            role="alert"
            aria-live="assertive"
          >
            {error}
          </div>
        )}

        {!loading && !error && (
          <>

            {/* 🔍 Manual Google Search Fallback */}
            {(brand || domain) && (
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-1">
                  Quick note: Some profiles may be slightly off - that’s a limitation of Google’s API. If the results seem off, you can search directly on Google instead:
                </p>
                <GoogleSearchButton brand={brand || domain} />
              </div>
            )}

            <ul className="space-y-3">
              {count === 0 ? (
                <li className="text-gray-500 text-sm">
                  {data?.message || "We couldn’t find any relevant prospects."}
                </li>
              ) : (
                list.map((p: Prospect, i: number) => (
                  <li
                    key={(p.linkedin_url || "item") + i}
                    className="border rounded-lg p-3 hover:bg-gray-50"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {p.image ? (
                          <img
                            src={p.image}
                            alt={p.name || "Profile"}
                            className="w-8 h-8 rounded-full object-cover border"
                          />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-xs text-gray-500">
                            ?
                          </div>
                        )}
                        <div>
                          <a
                            className="font-medium text-blue-700 hover:underline"
                            href={p.linkedin_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label={`Open LinkedIn profile: ${p.name || "Profile"}`}
                          >
                            {p.name || "Profile"}
                          </a>
                          <div className="text-xs text-gray-500 mt-0.5">
                            {p.designation || "—"}
                            {p.company ? <span> • {p.company}</span> : null}
                          </div>
                        </div>
                      </div>

                      {/* ✅ Category badges (only if available) */}
                      {p.category === "seo" && (
                        <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-700 font-medium">
                          SEO
                        </span>
                      )}
                      {p.category === "outreach" && (
                        <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-700 font-medium">
                          Outreach
                        </span>
                      )}
                    </div>
                  </li>
                ))
              )}
            </ul>
          </>
        )}
      </aside>
    </div>
  );
}
